import AppDialog from '../../components/app_dialog'
import { TextField } from '@cinesend/ui-kit'
import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import useStore from '../../hooks/use_store'

const emptyForm = {
  ife_code: '',
  bcp_47_code: '',
  iso_639_2_t_code: '',
  eng_description: '',
  local_description: '',
}

const LANGUAGE_QUERY_KEYS = [['languages'], ['/api/languages']]

const upsertLanguage = (list, language) => {
  const rows = Array.isArray(list) ? list : []
  const index = rows.findIndex((row) => row.id === language.id)
  if (index === -1) return [...rows, language]
  return rows.map((row, i) => (i === index ? { ...row, ...language } : row))
}

const LanguageFormModal = ({ language = null, onClose }) => {
  const isEdit = Boolean(language)
  const queryClient = useQueryClient()
  const { createModel, updateModel } = useStore()
  const [form, setForm] = useState(
    language
      ? {
          ife_code: language.ife_code ?? '',
          bcp_47_code: language.bcp_47_code ?? '',
          iso_639_2_t_code: language.iso_639_2_t_code ?? '',
          eng_description: language.eng_description ?? '',
          local_description: language.local_description ?? '',
        }
      : emptyForm
  )

  const update = (data) => setForm((prev) => ({ ...prev, ...data }))

  const isSaving = createModel.isLoading || updateModel.isLoading
  const canSave = form.ife_code.trim().length > 0 && form.eng_description.trim().length > 0

  const applyLanguageToCache = (saved) => {
    LANGUAGE_QUERY_KEYS.forEach((queryKey) => {
      queryClient.setQueryData(queryKey, (old) => upsertLanguage(old, saved))
    })
  }

  const save = () => {
    const data = {
      ife_code: form.ife_code.trim(),
      bcp_47_code: form.bcp_47_code.trim() || null,
      iso_639_2_t_code: form.iso_639_2_t_code.trim() || null,
      eng_description: form.eng_description.trim(),
      local_description: form.local_description.trim() || null,
    }

    if (isEdit) {
      updateModel.mutate({
        endpoint: 'languages',
        id: language.id,
        data,
        onSuccess: (res) => {
          applyLanguageToCache(res.data)
          onClose()
        },
      })
      return
    }

    createModel.mutate({
      endpoint: 'languages',
      data,
      onSuccess: (res) => {
        applyLanguageToCache(res.data)
        onClose()
      },
    })
  }

  return (
    <AppDialog
      title={isEdit ? 'Edit language' : 'Add language'}
      onClose={onClose}
      confirmText={isEdit ? 'Save' : 'Create'}
      onConfirm={save}
      confirmDisabled={!canSave || isSaving}
    >
      <div className='space-y-2'>
        <TextField
          label='English name'
          value={form.eng_description}
          onChange={(e) => update({ eng_description: e.target.value })}
          autoFocus
        />
        <TextField
          label='Local name'
          value={form.local_description}
          onChange={(e) => update({ local_description: e.target.value })}
        />
        <TextField label='IFE code' value={form.ife_code} onChange={(e) => update({ ife_code: e.target.value })} />
        <TextField label='BCP 47' value={form.bcp_47_code} onChange={(e) => update({ bcp_47_code: e.target.value })} />
        <TextField
          label='ISO 639-2/T'
          value={form.iso_639_2_t_code}
          onChange={(e) => update({ iso_639_2_t_code: e.target.value })}
        />
      </div>
    </AppDialog>
  )
}

export default LanguageFormModal
